import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  GraduationCap,
  Receipt,
} from 'lucide-react';
import { ModernCard, ModernCardBody, ModernCardHeader } from '@/components/ui/modern-card';
import { Skeleton } from '@/components/ui/skeleton';
import { useTodoDashboard } from '@/hooks/queries/useTodoDashboard';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { formatDA, formatDateDZ } from '@/lib/format';

// Sprint 6.9.18 — Widget "À traiter cette semaine".
// Liste compacte des actions en attente (factures en retard, plans en retard,
// sessions à confirmer). Cliquable → page concernée.

export function TodoDashboardWidget(): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const eurl =
    entreprises.find((e) => /kost/i.test(e.nom ?? '') && /eurl/i.test(e.nom ?? '')) ??
    entreprises[0] ??
    null;
  const [selected, setSelected] = useState<string>('');
  const effectiveEntrepriseId = selected || eurl?.id || null;

  const { data, isLoading } = useTodoDashboard(effectiveEntrepriseId);

  const total = data?.total ?? 0;

  return (
    <ModernCard variant="kpi">
      <ModernCardHeader
        icon={<AlertTriangle className="h-4 w-4 text-amber-600" />}
        title="À traiter cette semaine"
        subtitle={
          isLoading
            ? 'Chargement…'
            : total === 0
              ? 'Aucune action en attente'
              : `${total} action${total > 1 ? 's' : ''} à mener`
        }
        trailing={
          entreprises.length > 1 ? (
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
            >
              <option value="">Toutes</option>
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
          ) : undefined
        }
      />
      <ModernCardBody>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : total === 0 ? (
          <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
            <CheckCircle2 className="h-4 w-4" />
            <span>Tout est à jour. Bonne journée.</span>
          </div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-3">
            <Section
              icon={<Receipt className="h-3.5 w-3.5 text-rose-600" />}
              title="Factures en retard"
              count={data?.facturesEnRetard.length ?? 0}
              moreHref="/finances/recouvrement-entreprises"
              moreLabel="Voir Recouvrement"
              tone="rose"
            >
              {(data?.facturesEnRetard ?? []).slice(0, 4).map((f) => (
                <Link
                  key={f.id}
                  to="/finances/recouvrement-entreprises"
                  className="flex items-center justify-between gap-2 rounded-md border border-rose-100 bg-white px-2 py-1.5 text-xs hover:border-rose-300 hover:bg-rose-50/50"
                >
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-mono text-[10px] text-slate-500">
                      {f.reference_facture}
                    </span>{' '}
                    <span className="font-medium">{f.entreprise_cliente_nom}</span>
                  </span>
                  <span className="shrink-0 text-[10px] font-bold text-rose-700">
                    +{f.jours_retard}j
                  </span>
                  <span className="num shrink-0 font-semibold">{formatDA(f.montant_du)}</span>
                </Link>
              ))}
            </Section>

            <Section
              icon={<CalendarClock className="h-3.5 w-3.5 text-violet-600" />}
              title="Plans en retard"
              count={data?.plansEnRetard.length ?? 0}
              moreHref="/echeances/plans"
              moreLabel="Voir les plans"
              tone="violet"
            >
              {(data?.plansEnRetard ?? []).slice(0, 4).map((p) => (
                <Link
                  key={p.id}
                  to="/echeances/plans"
                  className="flex items-center justify-between gap-2 rounded-md border border-violet-100 bg-white px-2 py-1.5 text-xs hover:border-violet-300 hover:bg-violet-50/50"
                >
                  <span className="font-mono text-[10px] font-semibold text-violet-900">
                    {p.reference}
                  </span>
                  <span className="num shrink-0 font-semibold">{formatDA(p.montant_total)}</span>
                </Link>
              ))}
            </Section>

            <Section
              icon={<GraduationCap className="h-3.5 w-3.5 text-indigo-600" />}
              title="Sessions à confirmer"
              count={data?.sessionsAConfirmer.length ?? 0}
              moreHref="/formation/sessions"
              moreLabel="Voir les sessions"
              tone="indigo"
            >
              {(data?.sessionsAConfirmer ?? []).slice(0, 4).map((s) => (
                <Link
                  key={s.id}
                  to={`/formation/sessions/${s.id}`}
                  className="flex items-center justify-between gap-2 rounded-md border border-indigo-100 bg-white px-2 py-1.5 text-xs hover:border-indigo-300 hover:bg-indigo-50/50"
                >
                  <span className="font-mono text-[10px] font-semibold text-indigo-900">
                    {s.reference}
                  </span>
                  <span className="shrink-0 text-[10px] text-slate-600">
                    {formatDateDZ(s.date_debut)}
                  </span>
                  <span className="shrink-0 text-[10px] font-bold text-indigo-700">
                    {s.jours_avant === 0 ? "Aujourd'hui" : `J-${s.jours_avant}`}
                  </span>
                </Link>
              ))}
            </Section>
          </div>
        )}
      </ModernCardBody>
    </ModernCard>
  );
}

function Section({
  icon,
  title,
  count,
  moreHref,
  moreLabel,
  tone,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  moreHref: string;
  moreLabel: string;
  tone: 'rose' | 'violet' | 'indigo';
  children: React.ReactNode;
}): JSX.Element {
  const toneClasses: Record<string, string> = {
    rose: 'border-rose-200 bg-rose-50/30',
    violet: 'border-violet-200 bg-violet-50/30',
    indigo: 'border-indigo-200 bg-indigo-50/30',
  };
  return (
    <div className={`rounded-lg border ${toneClasses[tone]} p-2.5`}>
      <header className="mb-2 flex items-center justify-between">
        <h3 className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-700">
          {icon}
          {title}
          <span className="rounded-full bg-white px-1.5 text-[10px] font-bold text-slate-700 shadow-sm">
            {count}
          </span>
        </h3>
      </header>
      {count === 0 ? (
        <p className="text-[11px] text-muted-foreground">Rien à signaler.</p>
      ) : (
        <div className="space-y-1.5">
          {children}
          {count > 4 && (
            <Link
              to={moreHref}
              className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-600 hover:text-slate-900"
            >
              {moreLabel} ({count - 4} autres) <ChevronRight className="h-2.5 w-2.5" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
